<template>
    <main class="main-content">
        <InfoPopup v-model="showInfoPopup" :title="infoTitle" :showConfirmButton="showPopupConfirmButton"
            :showCancelButton="showPopupCancelButton" @confirm="handlePopupConfirm" @cancel="handlePopupCancel">
            <p>{{ infoMessage }}</p>
        </InfoPopup>
        <div class="subscription-panel">
            <div class="header">
                <div>
                    <h3>Subscription Plans</h3>
                    <p>Select a subscription plan to manage your account.</p>
                </div>
                <div class="credit-text-button" @click="goToCredit">Want More Credits?</div>
            </div>
            <div v-if='isLoggedIn' class="subscription-summary">
                <div class="summary-cards">
                    <div class="card current-plan">
                        <div class="card-tab current-tab">Current Plan</div>
                        <div class="card-content">
                            <div class="row">
                                <span>Subscription Type :</span>
                                <span class="bold">{{ currentPlan }}</span>
                            </div>
                            <div class="row" v-if="currentPlan !== 'Free'">
                                <span>Plan Period:</span>
                                <span class="bold">{{ planPeriod }}</span>
                            </div>
                            <div class="row">
                                <span>Status:</span>
                                <span class="bold">{{ subsStatus }}</span>
                            </div>
                            <button class="cancel-plan-btn" v-if="currentPlan !== 'Free'"
                                @click="onCancelPlanClicked">Cancel Plan at Period
                                End</button>
                        </div>
                    </div>
                    <div class="card next-payment">
                        <div class="card-tab next-tab">Next Payment</div>
                        <div class="card-content">
                            <div class="row">
                                <span>Amount:</span>
                                <span class="bold">{{ nextAmount }}</span>
                            </div>
                            <div class="row" v-if="!isNextPaymentZero">
                                <span>Date:</span>
                                <span class="bold">{{ nextDate }}</span>
                            </div>
                            <div class="row">
                                <span>Next Plan:</span>
                                <span class="bold">{{ nextPlan }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <main class="main-content">
                <div class="credit-options-panel">
                    <div class="period-tabs segmented" ref="tabsEl">
                        <button :class="{ active: selectedPeriod === 'year' }" @click="selectedPeriod = 'year'">
                            <span class="label">Yearly</span>
                            <span class="discount">-20% off</span>
                        </button>
                        <button :class="{ active: selectedPeriod === 'quarter' }" @click="selectedPeriod = 'quarter'">
                            <span class="label">Quarterly</span>
                            <span class="discount">-10% off</span>
                        </button>
                        <button :class="{ active: selectedPeriod === 'month' }" @click="selectedPeriod = 'month'">
                            <span class="label">Monthly</span>
                        </button>
                    </div>
                    <div class="plans-layout">
                        <div class="paid-column">
                            <div class="credit-grid paid-grid" ref="paidGridEl">
                                <div v-if="freePlan" class="credit-option disabled" ref="freeCardEl"
                                    :style="freeCardHeight ? { height: freeCardHeight + 'px' } : {}">
                                    <div class="credit-amount">{{ freePlan.name }}</div>
                                    <div class="credit-price">
                                        ${{ freePlan.price }}
                                    </div>
                                    <div class="plan-description">{{ freePlan.description }}</div>
                                    <ul>
                                        <li v-for="feature in freePlan.features" :key="feature">{{ feature }}</li>
                                    </ul>
                                </div>
                                <div v-for="plan in currentPaidPlans" :key="plan.price_id" class="credit-option"
                                    :class="{ selected: selectedPlan && selectedPlan.price_id === plan.price_id }"
                                    @click="onCardClick(plan)">
                                    <div class="credit-amount">{{ plan.name }}</div>
                                    <div class="credit-price">
                                        ${{ plan.price }}<span v-if="plan.period === 'year'">/yr</span><span
                                            v-else-if="plan.period === 'quarter'">/qrt</span><span v-else>/mo</span>
                                    </div>
                                    <div class="plan-description">{{ plan.description }}</div>
                                    <ul>
                                        <li v-for="feature in plan.features" :key="feature">{{ feature }}</li>
                                    </ul>
                                    <button class="subscribe-btn" :class="{
                                        next: buttonLabel(plan) === 'Next Plan',
                                        cancel: buttonLabel(plan) === 'Cancel Current Plan',
                                        selected: plan.price_id === currentPriceId,
                                    }" :disabled="plan.price_id === nextPriceId">
                                        {{ buttonLabel(plan) }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>

        <div v-if='isLoggedIn' class="payment-history-section">
            <h3>Payment History</h3>
            <div class="filter-panel" v-if="paymentHistory.length">
                <label>
                    Date:
                    <select v-model="selectedDateRange">
                        <option value="">All</option>
                        <option value="today">Today</option>
                        <option value="last_7_days">Last 7 Days</option>
                        <option value="last_30_days">Last 30 Days</option>
                        <option value="last_365_days">Last 365 Days</option>
                    </select>
                </label>
                <label>
                    Currency:
                    <select v-model="selectedCurrency">
                        <option value="">All</option>
                        <option v-for="cur in uniqueCurrencies" :key="cur" :value="cur">{{ cur }}</option>
                    </select>
                </label>
            </div>
            <table v-if="paginatedHistory.length" class="payment-history-table">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Product</th>
                        <th>Amount Paid</th>
                        <th>Currency</th>
                        <th>Price</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, idx) in paginatedHistory" :key="idx">
                        <td>{{ formatPaymentDate(item.paid_at) }}</td>
                        <td>{{ item.product }}</td>
                        <td>{{ formatPrice(item.amount_paid) }}</td>
                        <td>{{ (item.currency || '').toUpperCase() }}</td>
                        <td>{{ formatPrice(item.price) }}</td>
                    </tr>
                </tbody>
            </table>
            <div v-else class="no-payment-history">No payment history found.</div>
            <div class="pagination" v-if="totalPages > 1">
                <button @click="prevPage" :disabled="currentPage === 1">Prev</button>
                <span>Page {{ currentPage }} / {{ totalPages }}</span>
                <button @click="nextPage" :disabled="currentPage === totalPages">Next</button>
            </div>
        </div>
    </main>
</template>

<script>
import { ref, watch, onMounted, computed, nextTick, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { checkAuthentication, requireAuth } from '@/api/auth'
import { postSubPay, postSubCancel, getUserSubProduct, getPaymentHistory } from '@/api/credits';
import InfoPopup from '@/components/common/InfoPopup.vue';
import subscriptions from '@/data/subscriptions';
import subscriptionDescriptions from '@/data/SubscriptionDescriptions';
export default {
    name: 'PricingView',
    components: { InfoPopup },
    setup() {
        const isLoggedIn = ref(false);
        const route = useRoute()
        const status = ref(route.query.status || '')
        watch(
            () => route.query.status,
            val => { status.value = val || '' }
        )
        const showInfoPopup = ref(false);
        const infoTitle = ref('');
        const infoMessage = ref('');
        const showPopupConfirmButton = ref(true);
        const showPopupCancelButton = ref(true);
        const popupNextAction = ref('');
        const popupNextPlan = ref(null);

        const router = useRouter();
        const paidGridEl = ref(null);
        const freeCardEl = ref(null);
        const freeCardHeight = ref(null);
        const tabsEl = ref(null);
        const freeColMarginTop = ref(0);

        const allPlans = ref([]);
        const periodLabel = (p) => p ? 'Per ' + p.charAt(0).toUpperCase() + p.slice(1) : '';
        const descByName = Object.fromEntries(subscriptionDescriptions.map(d => [d.name, d]));
        const freePlan = ref(null);
        const plansByPeriod = ref({ month: [], quarter: [], year: [] });

        const mapped = subscriptions.map(s => {
            const info = descByName[s.name] || { description: '', features: [] };
            const displayName = s.name === 'Free' ? 'Free' : `${s.name} (${periodLabel(s.period)})`;
            return {
                name: displayName,
                baseName: s.name,
                period: s.period,
                price_id: s.price_id,
                price: typeof s.price === 'number' ? s.price.toFixed(2) : s.price,
                description: info.description,
                features: info.features || []
            };
        });
        allPlans.value = mapped;
        freePlan.value = mapped.find(p => p.baseName === 'Free') || null;
        plansByPeriod.value.month = mapped.filter(p => p.baseName !== 'Free' && p.period === 'month');
        plansByPeriod.value.quarter = mapped.filter(p => p.baseName !== 'Free' && (p.period === 'quarter' || p.period === 'quarterly'));
        plansByPeriod.value.year = mapped.filter(p => p.baseName !== 'Free' && (p.period === 'year' || p.period === 'annual' || p.period === 'yearly'));

        const selectedPeriod = ref('month');
        const currentPaidPlans = computed(() => plansByPeriod.value[selectedPeriod.value] || []);
        const selectedPlan = ref(null);
        const currentPriceId = ref(null);
        const nextPriceId = ref(null);

        const currentPlan = ref('-');
        const planPeriod = ref('-');
        const subsStatus = ref('-');
        const nextAmount = ref('-');
        const nextDate = ref('-');
        const nextPlan = ref('-');
        const nextAmountRaw = ref(0);

        const selectedDateRange = ref('');
        const selectedCurrency = ref('');
        const pageSize = 10;
        const currentPage = ref(1);

        const paymentHistory = ref([]);
        const uniqueCurrencies = computed(() => {
            const set = new Set(paymentHistory.value.map(p => (p.currency || '').toUpperCase()));
            return Array.from(set).filter(Boolean);
        });

        const filteredHistory = computed(() => {
            const now = new Date();
            const sorted = [...paymentHistory.value].sort((a, b) => new Date(b.paid_at) - new Date(a.paid_at));
            return sorted.filter(item => {
                let matchDate = true;
                if (selectedDateRange.value) {
                    const paidAt = new Date(item.paid_at);
                    if (selectedDateRange.value === 'today') {
                        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                        matchDate = paidAt >= today;
                    } else if (selectedDateRange.value === 'last_7_days') {
                        const daysAgo = new Date();
                        daysAgo.setDate(now.getDate() - 7);
                        matchDate = paidAt >= daysAgo;
                    } else if (selectedDateRange.value === 'last_30_days') {
                        const daysAgo = new Date();
                        daysAgo.setDate(now.getDate() - 30);
                        matchDate = paidAt >= daysAgo;
                    } else if (selectedDateRange.value === 'last_365_days') {
                        const daysAgo = new Date();
                        daysAgo.setDate(now.getDate() - 365);
                        matchDate = paidAt >= daysAgo;
                    }
                }
                let matchCurrency = true;
                if (selectedCurrency.value) {
                    matchCurrency = (item.currency || '').toUpperCase() === selectedCurrency.value;
                }
                return matchDate && matchCurrency;
            });
        });

        const totalPages = computed(() => Math.max(1, Math.ceil(filteredHistory.value.length / pageSize)));
        const paginatedHistory = computed(() => {
            const start = (currentPage.value - 1) * pageSize;
            return filteredHistory.value.slice(start, start + pageSize);
        });

        function nextPage() {
            if (currentPage.value < totalPages.value) currentPage.value++;
        }
        function prevPage() {
            if (currentPage.value > 1) currentPage.value--;
        }
        watch([filteredHistory], () => { currentPage.value = 1; });

        function formatPrice(val) {
            if (typeof val === 'number') return val.toFixed(2);
            const num = Number(val);
            return isNaN(num) ? val : num.toFixed(2);
        }
        function syncFreeCardHeight() {
            nextTick(() => {
                const container = paidGridEl.value;
                if (!container) return;
                const cards = Array.from(container.querySelectorAll('.credit-option'));
                let max = 0;
                for (const el of cards) {
                    max = Math.max(max, el.offsetHeight || 0);
                }
                if (max > 0) freeCardHeight.value = max;
            });
        }

        function syncFreeColumnOffset() {
            nextTick(() => {
                const el = tabsEl.value;
                if (!el) return;
                const styles = window.getComputedStyle(el);
                const mb = parseInt(styles.marginBottom || '0', 10) || 0;
                freeColMarginTop.value = (el.offsetHeight || 0) + mb;
            });
        }

        async function onCancelPlanClicked() {
            infoTitle.value = 'Cancel current plan';
            infoMessage.value = 'You are cancelling your current plan. You will be switched to the Free plan at the end of your current billing cycle.';
            showInfoPopup.value = true;
            popupNextAction.value = 'cancel';
        }
        async function onCardClick(plan) {
            if (!await requireAuth()) { return }
            if (plan.name === 'Free') return;
            if (!plan.price_id) {
                infoTitle.value = 'Plan unavailable';
                infoMessage.value = 'This subscription plan is not configured for this environment yet. Please contact support.';
                showInfoPopup.value = true;
                showPopupCancelButton.value = false;
                return;
            }
            if (currentPriceId.value != plan.price_id && currentPriceId.value != freePlan.value?.price_id) {
                infoTitle.value = 'Switching to a new plan';
                infoMessage.value = 'You are switching to a new paid plan.';
                showInfoPopup.value = true;
                popupNextAction.value = 'subscribe';
                popupNextPlan.value = plan;
            }
            else {
                handleSubscribe(plan);
            }

        }
        function handlePopupConfirm() {
            showInfoPopup.value = false;
            if (popupNextAction.value === 'cancel') {
                _handleCancelSubscription();
                popupNextAction.value = '';
                popupNextPlan.value = null;
            }
            else if (popupNextAction.value === 'subscribe') {
                if (popupNextPlan.value) {
                    handleSubscribe(popupNextPlan.value);
                }
                popupNextAction.value = '';
                popupNextPlan.value = null;
            }
        }
        function handlePopupCancel() {
            showInfoPopup.value = false;
            popupNextAction.value = '';
            popupNextPlan.value = null;
        }
        async function _handleCancelSubscription() {
            try {
                const response = await postSubCancel();
                const data = await response.json();
                if (data.type == 'cancel_scheduled') {
                    infoTitle.value = 'Cancellation Successful';
                    infoMessage.value = data.message;
                    showInfoPopup.value = true;
                    showPopupCancelButton.value = false;
                }
                await loadUserSubProduct();
            }
            catch (err) {
                infoTitle.value = 'Sorry, something went wrong';
                infoMessage.value = 'Please contact support for further assistance (Error starting subscription payment).';
                showInfoPopup.value = true;
                showPopupCancelButton.value = false;
            }
        }
        async function handleSubscribe(plan) {
            try {
                const response = await postSubPay(plan.price_id);
                if (!response.ok) throw new Error("Subscription payment failed");
                const data = await response.json();
                await loadUserSubProduct();
                if (data.type == 'new_subscription') {
                    window.location.href = data.checkout_url;
                }
                else if (data.type == 'cancellation') {
                    infoTitle.value = 'Cancellation Successful';
                    infoMessage.value = data.message;
                    showInfoPopup.value = true;
                    showPopupCancelButton.value = false;
                }
                else if (data.type == 'noop') {
                    infoTitle.value = 'Plan No Change';
                    infoMessage.value = data.message;
                    showInfoPopup.value = true;
                    showPopupCancelButton.value = false;
                }
                else if (data.type == 'plan_change') {
                    infoTitle.value = 'Plan Change Successful';
                    infoMessage.value = data.message;
                    showInfoPopup.value = true;
                    showPopupCancelButton.value = false;
                } else {
                    infoTitle.value = 'Sorry, something went wrong';
                    infoMessage.value = 'Please contact support for further assistance (Return type error).';
                    showInfoPopup.value = true;
                    showPopupCancelButton.value = false;
                }

            } catch (err) {
                infoTitle.value = 'Sorry, something went wrong';
                infoMessage.value = 'Please contact support for further assistance (Error starting subscription payment).';
                showInfoPopup.value = true;
                showPopupCancelButton.value = false;
            }
        }

        function buttonLabel(plan) {
            if (!plan) return 'Subscribe';
            if (plan.price_id === currentPriceId.value) return 'Current Plan';
            if (plan.price_id === nextPriceId.value) return 'Next Plan';
            return 'Subscribe';
        }

        function formatDate(dateStr) {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        }

        async function loadUserSubProduct() {
            console.log('loadUserSubProduct')
            try {
                const data = await getUserSubProduct();
                console.log('getUserSubProduct', data)
                currentPlan.value = data.active_plan_price_name;
                planPeriod.value = `${formatDate(data.active_plan_start_date)} - ${formatDate(data.active_plan_end_date)}`;
                subsStatus.value = 'Active';
                nextAmountRaw.value = data.next_plan_price;
                nextAmount.value = `$${data.next_plan_price.toFixed(2)}`;
                nextDate.value = formatDate(data.next_plan_start_date);
                nextPlan.value = data.next_plan_price_name;

                const subName = data[1]?.active_plan_price_name || data.active_plan_price_name || null;
                const nextSubName = data[1]?.next_plan_price_name || data.next_plan_price_name || null;
                if (subName) {
                    const matched = allPlans.value.find(p => p.name === subName);
                    currentPriceId.value = matched ? matched.price_id : null;
                } else {
                    currentPriceId.value = null;
                }
                if (nextSubName) {
                    const matched = allPlans.value.find(p => p.name === nextSubName);
                    nextPriceId.value = matched ? matched.price_id : null;
                }
            } catch (error) {
                if (error.response?.data?.message) {
                    throw new Error(error.response.data.message);
                }
                throw error;
            }
        }
        const isNextPaymentZero = computed(() => {
            return Number(nextAmountRaw.value) === 0;
        });

        async function loadPaymentHistory() {
            try {
                const data = await getPaymentHistory();
                paymentHistory.value = Array.isArray(data) ? data : [];
                console.log('paymentHistory.value', paymentHistory.value)
            } catch (error) {
                if (error.response?.data?.message) {
                    throw new Error(error.response.data.message);
                }
                throw error;
            }
        }
        function formatPaymentDate(dateStr) {
            if (!dateStr) return '';
            const iso = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
            const d = new Date(iso);
            return d.toLocaleString();
        }
        watch(status, val => {
            if (val === 'success') {
                infoTitle.value = 'Payment Successful';
                infoMessage.value = '🎉 Payment successful! Your subscription plan will be updated in the next payment cycle.';
                showInfoPopup.value = true;
            } else if (val === 'cancel') {
                infoTitle.value = 'Payment Canceled';
                infoMessage.value = '⚠️ Payment was canceled. No changes were made.';
                showInfoPopup.value = true;
            }
            if (val === 'success' || val === 'cancel') {
                router.replace({
                    query: { ...route.query, status: undefined }
                });
            }
        }, { immediate: true });
        watch(showInfoPopup, val => {
            if (val == false) { loadUserSubProduct(); }
        })
        watch(currentPaidPlans, () => syncFreeCardHeight());
        watch(selectedPeriod, () => {
            syncFreeCardHeight();
            syncFreeColumnOffset();
        });
        watch(selectedPeriod, () => syncFreeColumnOffset());

        onMounted(async () => {
            var { isAuthenticated } = await checkAuthentication();
            isLoggedIn.value = isAuthenticated;
            if (isLoggedIn.value) {
                loadUserSubProduct();
                loadPaymentHistory();
            }
            syncFreeCardHeight();
            syncFreeColumnOffset();
            window.addEventListener('resize', syncFreeCardHeight);
            window.addEventListener('resize', syncFreeColumnOffset);
        });

        onUnmounted(() => {
            window.removeEventListener('resize', syncFreeCardHeight);
            window.removeEventListener('resize', syncFreeColumnOffset);
        });

        function goToCredit() {
            router.push('/credit');
        }


        return {
            isLoggedIn,
            status,
            selectedPlan,
            currentPriceId,
            nextPriceId,
            buttonLabel,
            handleSubscription: handleSubscribe,
            goToCredit,
            showInfoPopup,
            infoTitle,
            infoMessage,
            loadUserSubProduct,
            currentPlan,
            planPeriod,
            subsStatus,
            nextAmount,
            nextDate,
            nextPlan,
            onCancelPlanClicked,
            onCardClick,
            freePlan,
            selectedPeriod,
            currentPaidPlans,
            paidGridEl,
            freeCardEl,
            freeCardHeight,
            tabsEl,
            freeColMarginTop,
            paymentHistory,
            formatPaymentDate,
            selectedDateRange,
            selectedCurrency,
            uniqueCurrencies,
            paginatedHistory,
            totalPages,
            currentPage,
            nextPage,
            prevPage,
            formatPrice,
            isNextPaymentZero,
            handlePopupConfirm,
            handlePopupCancel,
            showPopupCancelButton,
            showPopupConfirmButton,
        }

    }
};
</script>

<style scoped>
.main-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.subscription-panel {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    background-color: var(--background-white);
    padding: 2.5rem;
}

.header {
    border-bottom: 1px solid var(--border-color);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header h3 {
    margin: 0;
    font-weight: 600;
}

.main-content {
    display: flex;
    justify-content: center;
    width: 100%;
    margin: 0 auto;
    box-sizing: border-box;
}

.credit-options-panel {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.credit-grid,
.paid-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 20px;
    justify-items: center;
    width: 100%;
    box-sizing: border-box;
    padding-bottom: 0;
    overflow: visible;
}

.credit-option {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--border-color);
    border-radius: 12px;
    padding: 20px;
    text-align: left;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
    background: #fff;
    width: 100%;
    max-width: none;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.credit-option:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
    transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.credit-option>* {
    position: relative;
    z-index: 1;
    color: inherit;
}

.credit-amount {
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 4px;
    color: var(--text-primary);
}

.credit-price {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 8px;
}

.credit-option .plan-description {
    font-size: 13px;
    color: var(--text-secondary);
    margin-bottom: 12px;
    min-height: 40px;
}

.credit-option ul {
    padding-left: 0;
    margin-left: 0;
    list-style: none;
    font-size: 13px;
    color: var(--text-secondary);
    line-height: 28px;
    margin-bottom: 12px;
}

.credit-option ul li {
    position: relative;
    padding-left: 22px;
}

.credit-option ul li::before {
    content: '✓';
    position: absolute;
    left: 0;
    top: 0.4rem;
    line-height: 1.2;
    font-weight: 800;
    color: var(--purple);
}

button {
    transition: background 0.2s ease, transform 0.2s ease;
}

button:hover {
    transform: translateY(-2px);
}

button:disabled {
    cursor: not-allowed;
    transform: none;
}

.subscribe-btn {
    width: 100%;
    text-align: center;
    padding: 12px;
    font-size: 15px;
    border-radius: 8px;
    margin-top: auto;
    color: white;
    border: none;
    background-color: var(--purple);
    cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease;
}

.subscribe-btn.selected {
    background-color: #94a3b8;
}

.subscribe-btn:not(.selected):hover {
    transform: translateY(-2px);
}

.subscribe-btn:disabled {
    cursor: not-allowed;
    transform: none;
}

.credit-text-button {
    background: none;
    border: none;
    color: var(--text-primary);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    padding: 0;
}

.credit-text-button:hover {
    text-decoration: underline;
}

.summary-cards {
    display: flex;
    gap: 2rem;
    flex-wrap: wrap;
    margin-bottom: 2rem;
}

.card {
    flex: 1 1 320px;
    background: #fff;
    border: 1.5px solid #e5e7eb;
    border-radius: 12px;
    padding: 0;
    min-width: 320px;
    max-width: 420px;
    position: relative;
    box-sizing: border-box;
    overflow: visible;
}

.card-content {
    position: relative;
    padding: 2.2rem 1.5rem 1.5rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-top: 50px;
}

.card-tab {
    position: absolute;
    left: -1px;
    height: 38px;
    min-width: 140px;
    padding: 0 1.5rem;
    display: flex;
    align-items: center;
    border-top-left-radius: 12px;
    border-bottom-right-radius: 24px 16px;
    font-size: 0.86rem;
    font-weight: 600;
    color: #fff;
    z-index: 2;
}

.current-tab {
    background: url('@/assets/figs/pricing/pricing_current_plan_corner.png') no-repeat left center;
    background-size: cover;
    color: #fff;
}

.next-tab {
    background: url('@/assets/figs/pricing/pricing_next_plan_corner.png') no-repeat left center;
    background-size: cover;
    color: #fff;
}

.cancel-plan-btn {
    border: 1.5px solid #b800ff;
    color: var(--purple);
    background: #fff;
    padding: 0.5rem 1.2rem;
    border-radius: 6px;
    margin-top: 1.2rem;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;
}

.cancel-plan-btn:hover {
    background: #f9f3fc;
    color: var(--purple);
}

.subscribe-btn.next:disabled {
    background-color: #16a34a !important;
}

.subscribe-btn.cancel {
    background-color: #dc2626 !important;
}

.subscribe-btn.cancel:hover {
    background-color: #b91c1c !important;
}


.subscription-summary .card {
    display: flex;
    flex-direction: column;
}

.credit-option.disabled {
    cursor: default;
    pointer-events: none;
    opacity: 1;
    transform: none;
    box-shadow: none;
    border-color: var(--border-color);
}

.credit-option.disabled:hover {
    transform: none !important;
    box-shadow: none !important;
    border-color: var(--border-color) !important;
}

.credit-option.disabled:hover::before {
    content: none !important;
    display: none !important;
}

.credit-option.disabled * {
    color: inherit !important;
}

.credit-option.disabled:hover .credit-amount,
.credit-option.disabled:hover .credit-price,
.credit-option.disabled:hover .plan-description,
.credit-option.disabled:hover ul,
.credit-option.disabled:hover li {
    color: inherit !important;
}

.plans-layout {
    width: 100%;
}

.paid-column {
    display: block;
    min-width: 0;
}

.period-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
}

.period-tabs button {
    background: #f3f4f6;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: 6px 12px;
    cursor: pointer;
    font-weight: 600;
}

.period-tabs button.active {
    background: #2563eb;
    color: #fff;
    border-color: #2563eb;
}

.paid-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 20px;
    padding-bottom: 0;
    overflow: visible;
}

.paid-grid>.credit-option {
    z-index: 1;
    width: 100%;
    max-width: none;
    height: 100%;
}

.period-tabs.segmented {
    background-color: rgba(136, 0, 255, 0.03);
    border: 1px solid var(--purple);
    border-radius: 8px;
    padding: 6px;
    display: flex;
    align-items: center;
    gap: 6px;
    justify-content: center;
    width: 100%;
}

.period-tabs.segmented button {
    background: transparent;
    border: 2px solid transparent;
    border-radius: 16px;
    padding: 10px 18px;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-weight: 700;
    color: #111827;
    margin: 0.5rem;
}

.period-tabs.segmented button .label {
    color: var(--purple)
}

.period-tabs.segmented button .discount {
    color: #16a34a;
    font-weight: 800;
}

.period-tabs.segmented button.active {
    background-color: white;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.period-tabs.segmented button:not(.active) .label {
    color: #111827;
}

.period-tabs.segmented button:not(.active):hover {
    background: rgba(255, 255, 255, 0.6);
}

.payment-history-section {
    margin-top: 2rem;
    background: var(--background-white);
    padding: 2rem;
}

.payment-history-section h3 {
    text-align: left;
    margin-bottom: 1rem;
}

.payment-history-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
}

.payment-history-table th,
.payment-history-table td {
    text-align: left;
    padding: 1rem;
    border-bottom: 1px solid var(--border-color);
}

.payment-history-table th {
    font-size: 0.85rem;
    text-transform: uppercase;
    color: var(--text-secondary);
    font-weight: 600;
}

.no-payment-history {
    text-align: center;
    color: var(--text-secondary);
    margin: 12px 0;
    font-size: 14px;
}

.filter-panel {
    display: flex;
    gap: 2rem;
    justify-content: left;
    margin-bottom: 1rem;
    flex-wrap: wrap;
}

.filter-panel label {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-size: 14px;
    color: var(--text-secondary);
    gap: 6px;
}

.filter-panel select {
    margin-top: 4px;
    padding: 6px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
}

.pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin-top: 1rem;
}

.pagination button {
    padding: 6px 12px;
    border: 1px solid var(--border-color);
    background: var(--background-white);
    border-radius: 4px;
    cursor: pointer;
}

.pagination button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.row {
    display: flex;
    flex-direction: row;
}

.row>span.bold {
    margin-left: auto;
    text-align: right;
    display: block;
}
</style>
